import BreadCrumbs from 'components/breadcrumbs'
import { useState } from 'react'
import { Button, Card, CardBody, Col, Row } from 'reactstrap'
import { formatDatePMBV2 } from 'utils/formatDate'
import { ArrowLeft, ArrowRight, Search } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import LoadingAnimation from 'components/LoadingAnimation'
import { URL_MUTATION_REPORT } from 'utils/pathUrl'
import CustomButton from 'components/Buttons/CustomButton'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { useDebounce } from 'helpers'
import { useLazyFetchMutationReportQuery } from './mutationReportSlice'

const MutationReport = () => {
  const navigate = useNavigate()

  const [searchInput, setSearchInput] = useState('')
  const [dataLocal, setDataLocal] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)

  const [fetchMutationReport, { data, isLoading, isFetching, error }] =
    useLazyFetchMutationReportQuery()
  const debouncedSearch = useDebounce((value) => {
    if (!value?.trim()) return
    setHasSearched(true)
    fetchMutationReport({ search: value.trim() })
      .unwrap()
      .then((res) => {
        setDataLocal(res)
      })
      .catch(() => {
        setDataLocal(null)
      })
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    if (!value?.trim()) {
      setHasSearched(false)
      setDataLocal(null)
    }
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && searchInput.length === 0) {
      e.preventDefault()
    }
  }

  const typeLabelMap = {
    TRANSFER_IN: 'Transaksi',
    INVENTORY_GROUP: 'Packing',
    PROCESS: 'Process',
    INVENTORY_WAREHOUSE: 'Gudang',
  }

  const items = dataLocal?.items ?? []
  const mutationGroups = dataLocal?.mutationInventories ?? []

  // flatten mutation inventories
  const mutationInventories = mutationGroups.flatMap((g) => g.inventories ?? [])

  // group mutation by parentInventoryIds
  const mutationMap = mutationInventories.reduce((acc, inv) => {
    const parentIds = inv.parentInventoryIds?.split(',') ?? []

    parentIds.forEach((id) => {
      if (!acc[id]) acc[id] = []
      acc[id].push(inv)
    })

    return acc
  }, {})

  // merge items dengan mutations
  const mergedItems = items.map((item) => {
    const mutations = mutationMap[item.id] ?? []

    const totalMutationAmount = mutations.reduce(
      (sum, m) => sum + (m.amount ?? 0),
      0,
    )

    return {
      ...item,
      mutations,
      totalMutationAmount,
      mutationCount: mutations.length,
    }
  })

  const hasResults = mergedItems.length > 0

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Laporan Ketelusuran'} />
      </div>
      <div className="d-flex flex-column gap-2 mb-2">
        <Card className="border-0 shadow-sm">
          <CardBody>
            <h5 className="fw-semibold mb-1">Laporan Ketelusuran</h5>
            <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
              Lacak perjalanan inventori Anda dengan memasukkan Lot ID di bawah
              ini.
            </p>

            {/* Search by Lot ID */}
            <div className="mb-4" style={{ maxWidth: '400px' }}>
              <PMBSearchInputGroup
                label="Cari berdasarkan Lot ID"
                value={searchInput}
                placeholder="Contoh: TI.12575"
                onChange={(e) => onHandleSearchChange(e.target.value)}
                maxWidth="100%"
                onKeyDown={handleKeyDown}
                testId="mutation-search-lot-input"
              />
            </div>

            {/* Loading */}
            {(isLoading || isFetching) && (
              <div className="d-flex justify-content-center py-4">
                <LoadingAnimation />
              </div>
            )}

            {/* Initial state - no search yet */}
            {!hasSearched && !isLoading && !isFetching && (
              <div className="text-center py-5">
                <Search size={40} className="text-muted mb-3" />
                <p className="text-muted mb-0">
                  Masukkan Lot ID untuk melihat informasi ketelusuran inventori.
                </p>
              </div>
            )}

            {/* No result */}
            {hasSearched &&
              !isLoading &&
              !isFetching &&
              !hasResults &&
              !error && (
                <div className="text-center py-5">
                  <Search size={40} className="text-muted mb-3" />
                  <p className="fw-semibold mb-1">Data tidak ditemukan</p>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                    Lot ID <strong>&quot;{searchInput}&quot;</strong> tidak
                    ditemukan. Pastikan Lot ID yang Anda masukkan sudah benar.
                  </p>
                </div>
              )}

            {/* Error */}
            {error && (
              <div className="text-center py-5">
                <p className="fw-semibold text-danger mb-1">
                  Terjadi Kesalahan
                </p>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Gagal mengambil data. Silakan periksa koneksi Anda dan coba
                  lagi.
                </p>
              </div>
            )}

            {/* Results */}
            {searchInput && hasResults && !isLoading && !isFetching && (
              <>
                <hr className="my-4" />
                <h6 className="fw-semibold mb-1">Hasil Pencarian</h6>
                <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                  Ditemukan {mergedItems.length} data ketelusuran. Pilih Asal
                  Barang atau Tujuan Barang untuk melihat detail lebih lanjut.
                </p>

                {items.map((item, index) => (
                  <Card
                    key={item?.id ?? index}
                    className="shadow-sm border mb-3"
                    data-testid={`mutation-result-card-${item?.id}`}
                  >
                    <CardBody>
                      <Row className="mb-3">
                        <Col xs="12" sm="3" className="fw-light text-sm">
                          Tanggal
                        </Col>
                        <Col xs="12" sm="9" className="fw-semibold text-sm">
                          {formatDatePMBV2(item?.createdAt) || '-'}
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col xs="12" sm="3" className="fw-light text-sm">
                          Lot ID
                        </Col>
                        <Col xs="12" sm="9" className="fw-semibold text-sm">
                          {item?.lotCode ?? '-'}
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col xs="12" sm="3" className="fw-light text-sm">
                          Tipe
                        </Col>
                        <Col xs="12" sm="9" className="fw-semibold text-sm">
                          <div
                            className="badge rounded-pill bg-success-50 text-success px-2"
                            style={{
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              textAlign: 'center',
                              maxWidth: '100%',
                            }}
                          >
                            {typeLabelMap[item?.eventName] || '-'}
                          </div>
                        </Col>
                      </Row>
                      {/* <Row className="mb-3">
                        <Col xs="12" sm="3" className="fw-light text-sm">
                          Pelaku
                        </Col>
                        <Col xs="12" sm="9" className="fw-semibold text-sm">
                          <div className="d-inline-flex align-items-center gap-2">
                            {item?.creator?.fullName || '-'}
                          </div>
                          {item?.creator?.partnership?.name && (
                            <div
                              className="badge rounded-pill text-primary bg-gray-100 font-semibold mt-2"
                              style={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                textAlign: 'center',
                                maxWidth: '100%',
                              }}
                            >
                              {item?.creator?.partnership?.name ?? ''}
                            </div>
                          )}
                        </Col>
                      </Row> */}

                      {/* Trace direction per item */}
                      <hr className="my-3" />
                      <div
                        className="d-flex flex-wrap gap-3"
                        data-testid={`mutation-detail-button-${item?.id}`}
                      >
                        <div>
                          <CustomButton
                            color="primary"
                            size="sm"
                            testId="mutation-direction-tab-backward"
                            onClick={() => {
                              navigate(
                                `${URL_MUTATION_REPORT}/${item?.id}?direction=BACKWARD`,
                                {
                                  state: {
                                    rowData: item,
                                    direction: 'BACKWARD',
                                  },
                                },
                              )
                            }}
                          >
                            <ArrowLeft size={14} className="me-1" />
                            Lihat Asal Barang
                          </CustomButton>
                          <p
                            className="text-muted mt-1 mb-0"
                            style={{ fontSize: '12px' }}
                          >
                            Dari mana barang di lot ini berasal
                          </p>
                        </div>
                        <div>
                          <CustomButton
                            color="primary"
                            outline
                            size="sm"
                            testId="mutation-direction-tab-forward"
                            onClick={() => {
                              navigate(
                                `${URL_MUTATION_REPORT}/${item?.id}?direction=FORWARD`,
                                {
                                  state: {
                                    rowData: item,
                                    direction: 'FORWARD',
                                  },
                                },
                              )
                            }}
                          >
                            Lihat Tujuan Barang
                            <ArrowRight size={14} className="ms-1" />
                          </CustomButton>
                          <p
                            className="text-muted mt-1 mb-0"
                            style={{ fontSize: '12px' }}
                          >
                            Ke mana barang di lot ini digunakan atau dikirim
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default MutationReport
